import { Fragment, createElement, type ReactNode } from "react";

// =============================================================================
// Markdown — rendu léger et sûr (pas de HTML brut) des corps d'articles.
//
// Supporte : titres (##, ###), listes à puces (-) et numérotées (1.),
// citations (>), tableaux (| a | b |), blocs de code (```), paragraphes,
// et inline **gras**, *italique*, `code`, [lien](url).
// Volontairement minimal et sûr : aucun HTML brut n'est interprété.
// =============================================================================

interface MarkdownProps {
  source: string;
  className?: string;
}

export function Markdown({ source, className }: MarkdownProps) {
  return <div className={className}>{renderBlocks(source)}</div>;
}

function renderBlocks(src: string): ReactNode[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";

    // Ligne vide -> séparateur
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Bloc de code (``` ... ```)
    if (/^```/.test(line.trim())) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test((lines[i] ?? "").trim())) {
        code.push(lines[i] ?? "");
        i++;
      }
      i++; // saute la fence fermante
      blocks.push(
        <pre
          key={key++}
          className="my-3 overflow-x-auto rounded-lg bg-bg-inverse/90 px-3.5 py-3 font-mono text-xs leading-relaxed text-bg"
        >
          {code.join("\n")}
        </pre>,
      );
      continue;
    }

    // Tableau (| col | col |) — la ligne séparatrice |---|---| est ignorée
    if (/^\s*\|.+\|\s*$/.test(line)) {
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|.+\|\s*$/.test(lines[i] ?? "")) {
        const raw = (lines[i] ?? "").trim();
        i++;
        if (/^\|[\s:-]+\|$/.test(raw.replace(/[^|\s:-]/g, "x")) && /^[\s|:-]+$/.test(raw)) continue;
        rows.push(
          raw
            .replace(/^\|/, "")
            .replace(/\|$/, "")
            .split("|")
            .map((c) => c.trim()),
        );
      }
      if (rows.length > 0) {
        const [head, ...body] = rows;
        blocks.push(
          <div key={key++} className="my-3 overflow-x-auto rounded-lg border border-line">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-bg-subtle">
                  {(head ?? []).map((c, idx) => (
                    <th
                      scope="col"
                      key={idx}
                      className="px-3 py-2 text-left font-semibold text-ink"
                    >
                      {renderInline(c)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((row, rIdx) => (
                  <tr key={rIdx} className="border-b border-line/60 last:border-0">
                    {row.map((c, cIdx) => (
                      <td key={cIdx} className="px-3 py-2 text-ink-muted">
                        {renderInline(c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        );
      }
      continue;
    }

    // Titres
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1]?.length ?? 1;
      const text = heading[2] ?? "";
      const cls =
        level <= 2
          ? "font-display text-lg font-semibold text-ink mt-6 first:mt-0"
          : "font-display text-base font-semibold text-ink mt-5 first:mt-0";
      blocks.push(
        createElement(
          `h${Math.min(6, Math.max(3, level + 1))}`,
          { key: key++, className: cls },
          renderInline(text),
        ),
      );
      i++;
      continue;
    }

    // Citation (peut s'étendre sur plusieurs lignes consécutives)
    if (/^>\s?/.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i] ?? "")) {
        quote.push((lines[i] ?? "").replace(/^>\s?/, ""));
        i++;
      }
      blocks.push(
        <blockquote
          key={key++}
          className="my-4 rounded-r-lg border-l-4 border-brand bg-brand-muted/40 px-4 py-3 text-sm italic text-ink"
        >
          {renderInline(quote.join(" "))}
        </blockquote>,
      );
      continue;
    }

    // Liste à puces
    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^[-*]\s+/, ""));
        i++;
      }
      blocks.push(
        <ul key={key++} className="my-3 list-disc space-y-1.5 pl-5 text-sm text-ink-muted">
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // Liste numérotée
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push(
        <ol key={key++} className="my-3 list-decimal space-y-1.5 pl-5 text-sm text-ink-muted">
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    // Paragraphe (accumule jusqu'à une ligne vide ou un bloc spécial)
    const para: string[] = [];
    while (i < lines.length) {
      const cur = lines[i] ?? "";
      if (
        cur.trim() === "" ||
        /^(#{1,4})\s+/.test(cur) ||
        /^>\s?/.test(cur) ||
        /^[-*]\s+/.test(cur) ||
        /^\d+\.\s+/.test(cur) ||
        /^\s*\|.+\|\s*$/.test(cur) ||
        /^```/.test(cur.trim())
      ) {
        break;
      }
      para.push(cur);
      i++;
    }
    blocks.push(
      <p key={key++} className="my-3 text-sm leading-relaxed text-ink-muted">
        {renderInline(para.join(" "))}
      </p>,
    );
  }

  return blocks;
}

// Inline : **gras**, *italique*, `code`, [texte](url)
function renderInline(text: string): ReactNode {
  const nodes: ReactNode[] = [];
  let rest = text;
  let key = 0;

  const pattern = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(\[([^\]]+)\]\(([^)]+)\))|(`([^`]+)`)/;

  while (rest.length > 0) {
    const m = pattern.exec(rest);
    if (!m) {
      nodes.push(<Fragment key={key++}>{rest}</Fragment>);
      break;
    }
    if (m.index > 0) {
      nodes.push(<Fragment key={key++}>{rest.slice(0, m.index)}</Fragment>);
    }
    if (m[1]) {
      nodes.push(
        <strong key={key++} className="font-semibold text-ink">
          {m[2]}
        </strong>,
      );
    } else if (m[3]) {
      nodes.push(
        <em key={key++} className="italic">
          {m[4]}
        </em>,
      );
    } else if (m[5]) {
      const rawHref = m[7] ?? "";
      const safeHref = /^https?:\/\/|^\//.test(rawHref) ? rawHref : "#";
      nodes.push(
        <a
          key={key++}
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-brand underline underline-offset-2 hover:opacity-80"
        >
          {m[6]}
        </a>,
      );
    } else if (m[8]) {
      nodes.push(
        <code
          key={key++}
          className="rounded bg-bg-subtle px-1.5 py-0.5 font-mono text-[0.85em] text-ink"
        >
          {m[9]}
        </code>,
      );
    }
    rest = rest.slice(m.index + m[0].length);
  }

  return nodes;
}
